import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import {
  Filter,
  AlertCircle,
  X,
  ArrowUpDown,
  Camera,
  Loader2,
  Search,
  ChevronRight,
  MapPin,
  Bell,
  Moon,
  AppWindow,
  Shield,
  HelpCircle,
  FileText,
  User,
  CreditCard,
  Calendar,
  RefreshCw,
  Smartphone,
  CheckCircle,
} from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/client';
import { Scanner } from '@yudiel/react-qr-scanner';
import Header from '@/components/common/Header';
import CallCard from '@/components/common/CallCard';
import TaskCard from '@/components/common/TaskCard';
import ImpoundCard from '@/components/common/ImpoundCard';
import InvoiceCard from '@/components/common/InvoiceCard';
import { SearchInputWithCustomKeyboard } from '@/components/common/SearchInputWithCustomKeyboard';
import { ActiveServiceCallsTable } from '@/components/ActiveServiceCallsTable';
import BottomNavigation from '@/components/navigation/BottomNavigation';
import CallScreen from '@/screens/calls/CallScreen';
import AllCallsScreen from '@/screens/calls/AllCallsScreen';
import CreateCallScreen from '@/screens/calls/CreateCallScreen';
import CreateCallBar from '@/components/common/CreateCallBar';
import CreateDriverScreen from '@/screens/drivers/CreateDriverScreen';
import AllTasksScreen from '@/screens/tasks/AllTasksScreen';
import ImpoundScreen from '@/screens/impounds/ImpoundScreen';
import PaymentScreen from '@/screens/payments/PaymentScreen';
import DriversScreen from '@/screens/drivers/DriversScreen';
import AllDispatchScreen from '@/screens/dispatch/AllDispatchScreen';
import AnalyticsScreen from '@/screens/analytics/AnalyticsScreen';
import { DriverPerformanceScreen } from '@/screens/drivers/DriverPerformanceScreen';
import { usePermissions } from '@/hooks/usePermissions';
import { IOSInstallPrompt } from '@/components/common/IOSInstallPrompt';
import { MobileAppDownloadPrompt } from '@/components/common/MobileAppDownloadPrompt';
import { useDriverStateV2 } from '@/hooks/useDriverStateV2';
import AuthErrorScreen from '@/components/auth/AuthErrorScreen';
import { useDriverImpounds } from '@/hooks/useDriverImpounds';
import { prefetchDriverImpound } from '@/hooks/useDriverImpound';
import { prefetchVehicleImages } from '@/hooks/useVehicleImages';
import { useInvoices } from '@/hooks/useInvoices';
import { useReleasedImpoundsCount } from '@/hooks/useReleasedImpoundsCount';
import {
  useDriverPermissions,
  useHasPermission,
} from '@/hooks/useDriverPermissions';
import { useDriverTabs } from '@/hooks/useDriverTabs';
import PermissionsErrorAlert from '@/components/common/PermissionsErrorAlert';
import { initialTab } from '@/lib/driverAppTabs';
import { useGetCustomers } from '@/api/hooks/useGetCustomers';
import { useGetVehicleMakes } from '@/api/hooks/useGetVehicleMakes';
import { useMobileAppPrompt } from '@/hooks/useMobileAppPrompt';
import { useOfficeRole } from '@/hooks/useOfficeRole';
import { useCompanyDrivers } from '@/hooks/useCompanyDrivers';
import env from '@/config/env';
import { useUserCompanies } from '@/hooks/useUserCompanies';
import CompanySelect from '@/components/common/CompanySelect.tsx';
import { useCompanyDispatches } from '@/hooks/useCompanyDispatches';
import { useHybridAuth } from '@/hooks/useHybridAuth';
import { driverApi } from '@/services/driverApi';
// import { useNewCallDetection } from '@/hooks/useNewCallDetection'; // Temporarily disabled
// import { useUpdateCallStatus } from '@/hooks/useUpdateCallStatus'; // Temporarily disabled
// import { useUpdateDriverStatus } from '@/hooks/useUpdateDriverStatus'; // Temporarily disabled
import { useNotifications } from '@/hooks/useNotifications';
// import NewCallPopup from '@/components/common/NewCallPopup'; // Temporarily disabled
import { Call, Task, SortOption, mapDriverImpoundToImpound } from '@/types';
import { useAuth0 } from '@auth0/auth0-react';
import {
  isEmbeddedMode,
  setEmbeddedMode,
  getEmbeddedModeDebugInfo,
} from '@/utils/embeddedMode';
import {
  shouldShowOnboarding,
  ONBOARDING_COMPLETED_KEY,
} from '@/utils/onboardingGate';
import { getDriverDisplayName } from '@/utils/driverName';

type FilterOption = 'All' | 'Calls' | 'Tasks' | 'Recents';
type CallsFilterOption = 'Active Only' | 'Show Completed' | 'Last 7 Days';
type TabType =
  | 'home'
  | 'impounds'
  | 'calls'
  | 'invoices'
  | 'scanner'
  | 'drivers'
  | 'analytics';

interface HomeScreenProps {
  dataPreloaded?: boolean;
  preloadError?: string | null;
  preloadedData?: { profile: any; calls: any[] } | null;
  // Optional display props
  showHeader?: boolean;
  showMenu?: boolean;
  showSettings?: boolean;
  headerTitle?: string;
  dataLoadError?: boolean;
}

const HomeScreen = (props: HomeScreenProps) => {
  // Extract display options with defaults
  const {
    showHeader = true,
    showMenu = true,
    showSettings = true,
    headerTitle,
    dataLoadError: _dataLoadError = false,
    // Keep existing props for backward compatibility but mark as unused
    dataPreloaded: _dataPreloaded,
    preloadError: _preloadError,
    preloadedData: _preloadedData,
  } = props;

  // Determine embedded mode using persistent detection
  const isEmbedded = isEmbeddedMode();
  const effectiveShowMenu = showMenu && !isEmbedded; // Override showMenu if embedded mode is detected

  // Mobile app download prompt
  const {
    showPrompt: showMobileAppPrompt,
    deviceInfo: mobileDeviceInfo,
    handleDismiss: handleMobilePromptDismiss,
  } = useMobileAppPrompt();

  const {
    driver,
    calls,
    tasks,
    location: _location,
    locationError,
    isLocationLoading,
    isDataLoading,
    dataError,
    authError,
    sessionError,
    actions,
  } = useDriverStateV2();

  // The office tabs or the field tabs; the owner's own corners (payments,
  // onboarding) go by isOwner. Neither is what a member may do: that is
  // usePermissions. The tabs are fixed sets (useDriverTabs).
  const officeRole = useOfficeRole();
  const isOwner = officeRole === 'OWNER';
  const hasOfficeRole = !!officeRole;
  const perms = usePermissions();
  const navigate = useNavigate();
  const isFetchingUserCompanies = useIsFetching({
    queryKey: ['user-companies', driver?.userId],
  });
  const userCompaniesResult = useUserCompanies();

  const { logout, user } = useAuth0();

  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [callOriginTab, setCallOriginTab] = useState<TabType | null>(null);
  const [selectedImpound, setSelectedImpound] = useState<string | null>(null);
  const [selectedPaymentDispatch, setSelectedPaymentDispatch] = useState<
    string | null
  >(null);
  const [selectedDriver, setSelectedDriver] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showCreateCall, setShowCreateCall] = useState(false);
  const [filterOption, setFilterOption] = useState<FilterOption>('All');
  const [callsFilterOption, setCallsFilterOption] =
    useState<CallsFilterOption>('Active Only');
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('calls'); // Default to calls tab
  const [showSettingsOverlay, setShowSettingsOverlay] = useState(false);
  const [showHelpScreen, setShowHelpScreen] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('Latest');
  const [showSortPopup, setShowSortPopup] = useState(false);
  const [invoiceSortOption, setInvoiceSortOption] = useState<
    'Latest' | 'Oldest'
  >('Latest');
  const [showInvoiceSortPopup, setShowInvoiceSortPopup] = useState(false);
  const [driversSortOption, setDriversSortOption] = useState<
    'A-Z' | 'Z-A' | 'Status'
  >('A-Z');
  const [showDriversSortPopup, setShowDriversSortPopup] = useState(false);
  const [showCreateDriver, setShowCreateDriver] = useState(false);
  const [qrResult, setQrResult] = useState<string>('');
  const [isScanning, setIsScanning] = useState(false);
  const [pendingQrCode, setPendingQrCode] = useState<string | null>(null);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showAllDispatch, setShowAllDispatch] = useState(false);
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [impoundsSearchQuery, setImpoundsSearchQuery] = useState<string>('');
  const locationWatchId = useRef<number | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const sortButtonRef = useRef<HTMLButtonElement>(null);
  const invoiceSortButtonRef = useRef<HTMLButtonElement>(null);
  const driversSortButtonRef = useRef<HTMLButtonElement>(null);
  const { getAccessTokenSilently } = useHybridAuth();

  // Track if initial tab has been set from the tabs
  const hasSetInitialTab = useRef(false);

  const { tabs } = useDriverTabs();
  const canCreateInvoice = useHasPermission('invoices.create');
  const canEditStatus = useHasPermission('drivers.edit_status');
  const permissionsState = useDriverPermissions();
  const hasHomeScreen = tabs.includes('home');
  const hasDriversScreen = tabs.includes('drivers');
  const hasAnalyticsScreen = tabs.includes('analytics');

  // A screen on its own route (inspections) sends the tab it wants opened,
  // since the tabs live in this component's state and not in the URL.
  const location = useLocation();
  const askedForTab = (location.state as { tab?: string } | null)?.tab;

  // Set initial / corrected tab from the tabs
  useEffect(() => {
    if (tabs.length === 0) return;
    if (!hasSetInitialTab.current) {
      const wanted = initialTab(
        tabs,
        askedForTab ?? (hasOfficeRole ? 'home' : 'calls')
      );
      // Inspections is a route, not a tab here, so landing on it would draw an
      // empty screen. This happens to anyone whose permissions leave no tab
      // above it in the list.
      if (wanted === 'inspections') {
        hasSetInitialTab.current = true;
        navigate('/inspections', { replace: true });
        return;
      }
      setActiveTab(wanted as TabType);
      hasSetInitialTab.current = true;
      return;
    }
    if (!tabs.includes(activeTab)) {
      const next = initialTab(tabs);
      if (next === 'inspections') {
        navigate('/inspections', { replace: true });
        return;
      }
      setActiveTab(next as TabType);
    }
  }, [tabs, hasOfficeRole, activeTab, askedForTab, navigate]);

  // New call detection and mutation hooks - temporarily disabled
  // const { newCall, clearNewCall } = useNewCallDetection(calls);
  // const newCall = null; // Temporarily disable new call popup
  // const clearNewCall = () => {}; // Empty function as placeholder
  // const updateCallStatusMutation = useUpdateCallStatus();
  // const updateDriverStatusMutation = useUpdateDriverStatus();
  const { showNotification } = useNotifications();

  // Impound data fetching
  const {
    data: impoundData,
    isLoading: isImpoundsLoading,
    error: _impoundsError,
  } = useDriverImpounds({
    status: sortOption === 'Completed by you' ? 'ALL' : 'ACTIVE',
    sortBy:
      sortOption === 'Latest'
        ? 'newest'
        : sortOption === 'Oldest'
          ? 'oldest'
          : 'newest',
    pageSize: 50, // Get more impounds for better UX
  });

  // One invoice list for every role. Owners and dispatchers see the
  // company's; a driver sees invoices on the calls assigned to them. That
  // used to be two endpoints, both requested for everyone, one of them
  // shown.
  const {
    data: invoiceData,
    isLoading: _isInvoicesLoading,
    error: _invoicesError,
  } = useInvoices({
    sortBy: invoiceSortOption === 'Latest' ? 'newest' : 'oldest',
    assignedToMe: !(hasOfficeRole || hasHomeScreen),
    pageSize: 100,
  });

  // Fetch released impounds count for home tab
  const {
    data: releasedImpoundsCount = 0,
    isLoading: _isReleasedImpoundsLoading,
  } = useReleasedImpoundsCount();

  // Whoever drives for the company (for Home tab - Drivers On Call section)
  const { data: companyDrivers = [], isLoading: companyDriversLoading } =
    useCompanyDrivers({ activeTab: 'home' });
  // Drivers the owner has added: a new account's owner drives too, so
  // counting them would end onboarding before it began.
  const otherDriversCount = companyDrivers.filter(
    (companyDriver) => companyDriver.id !== driver?.id
  ).length;

  // Fetch company-wide dispatches (for Home tab - Daily Summary and Calls cards)
  const {
    data: companyDispatches = [],
    isLoading: dispatchesLoading,
    error: _dispatchesError,
  } = useCompanyDispatches();

  // New owner accounts: send to onboarding until they have a driver or a dispatch, or they skip/complete
  useEffect(() => {
    const onboardingCompleted =
      typeof window !== 'undefined' &&
      localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true';

    if (
      shouldShowOnboarding({
        hasDriverId: !!driver?.userId,
        hasCompanyId: !!driver?.companyId,
        isFetchingUserCompanies: !!isFetchingUserCompanies,
        isOwner,
        companyDriversLoading,
        dispatchesLoading,
        otherDriversCount,
        dispatchesCount: companyDispatches.length,
        onboardingCompleted,
      })
    ) {
      navigate('/onboarding', { replace: true });
    }
  }, [
    driver?.userId,
    driver?.companyId,
    isFetchingUserCompanies,
    isOwner,
    companyDriversLoading,
    dispatchesLoading,
    otherDriversCount,
    companyDispatches.length,
    navigate,
  ]);

  // Preload vehicle makes on component mount for faster dropdown load times
  // This will hit the cache if available, otherwise fetch from API
  const { data: vehicleMakesData } = useGetVehicleMakes();

  // Get customers data for call display
  const { data: customersData } = useGetCustomers();

  // Removed local notification - now relying on push notifications only
  // This prevents duplicate notifications (local + push)

  // Log when vehicle makes are loaded for debugging cache performance
  useEffect(() => {
    if (vehicleMakesData && vehicleMakesData.length > 0) {
      console.log(
        `✅ [HomeScreen] Vehicle makes preloaded: ${vehicleMakesData.length} makes available`
      );
    }
  }, [vehicleMakesData]);

  // Request location when component mounts
  useEffect(() => {
    // In embedded mode, use Android location service instead of browser geolocation
    if (isEmbedded) {
      console.log('Embedded mode: Using Android location service');

      // Set up QR scanner callback for Android interface
      (window as any).onQRCodeScanned = (qrCode: string) => {
        const startTime = performance.now();
        console.log('📱 [Android QR] QR Code scanned from Android:', qrCode);
        console.log('📱 [Android QR] Current active tab:', activeTab);
        console.log(
          '📱 [Android QR] Current selected impound:',
          selectedImpound
        );
        console.log('📱 [Android QR] Impound data available:', !!impoundData);
        console.log(
          '📱 [Android QR] Impound data length:',
          impoundData?.data?.length || 0
        );
        console.log('📱 [Android QR] Is impounds loading:', isImpoundsLoading);

        setQrResult(qrCode);
        setIsScanning(false);

        // Check if impound data is ready
        if (
          !isImpoundsLoading &&
          impoundData?.data &&
          impoundData.data.length > 0
        ) {
          // Data is ready, process immediately
          console.log(
            '📱 [Android QR] Data ready, processing QR code immediately'
          );
          handleQrScan([{ rawValue: qrCode }]);
        } else {
          // Data not ready, store as pending
          console.log(
            '📱 [Android QR] Data not ready, storing QR code as pending'
          );
          setPendingQrCode(qrCode);
        }

        const duration = performance.now() - startTime;
        console.log(
          `📱 [Android QR] QR code processing took ${duration.toFixed(2)}ms`
        );
      };

      // Set up QR scanner cancellation callback for Android interface
      (window as any).onQRScannerCancelled = () => {
        const startTime = performance.now();
        console.log(
          'QR Scanner cancelled from Android - resetting scanner state'
        );
        setIsScanning(false);
        setQrResult(''); // Clear any previous results
        setPendingQrCode(null); // Clear any pending QR codes
        const duration = performance.now() - startTime;
        console.log(
          `QR scanner cancellation processing took ${duration.toFixed(2)}ms`
        );
      };

      // Cleanup function
      return () => {
        // Clean up Android/iOS callbacks
        delete (window as any).onQRCodeScanned;
        delete (window as any).onQRScannerCancelled;
      };
    } else {
      // Normal mode: use browser geolocation
      // Request initial location
      actions.requestLocation();

      // Start continuous location tracking
      const watchId = actions.startLocationTracking();
      locationWatchId.current = watchId;

      // Cleanup function to stop location tracking
      return () => {
        if (locationWatchId.current !== null) {
          actions.stopLocationTracking(locationWatchId.current);
        }
      };
    }
  }, [actions, isEmbedded]);

  // Process pending QR code when impound data becomes available
  useEffect(() => {
    if (
      pendingQrCode &&
      !isImpoundsLoading &&
      impoundData?.data &&
      impoundData.data.length > 0
    ) {
      console.log(
        '🔄 [Pending QR] Processing pending QR code now that data is available:',
        pendingQrCode
      );
      setPendingQrCode(null); // Clear the pending code first
      handleQrScan([{ rawValue: pendingQrCode }]);
    }
  }, [pendingQrCode, isImpoundsLoading, impoundData]);

  const handleMenuClick = () => {
    console.log('Menu clicked');

    // In embedded mode, call openSettings() instead of opening overlay
    if (isEmbedded) {
      console.log(
        'Embedded mode: triggering openSettings() from hamburger menu'
      );
      if (typeof (window as any).openSettings === 'function') {
        (window as any).openSettings();
      } else {
        console.warn('openSettings function not available in embedded mode');
      }
    } else {
      // Normal mode: open settings overlay
      if (showSettings) {
        setShowSettingsOverlay(true);
      }
    }
  };

  const handleStatusClick = () => {
    console.log('Status clicked');
  };

  const handleStatusChange = (
    newStatus:
      | 'DISPATCHED'
      | 'WAITING'
      | 'EN_ROUTE'
      | 'ON_SCENE'
      | 'TOWING'
      | 'RETURNING'
      | 'OFFLINE'
  ) => {
    console.log('Status changed to:', newStatus);

    // Update the driver's status in the database
    // Temporarily disabled updateDriverStatusMutation
    console.log('Status update functionality temporarily disabled');

    // Show success notification anyway for UX
    showNotification({
      title: 'Status Updated',
      body: `Your status has been updated to ${getStatusDisplayName(newStatus)}`,
      playSound: false,
    });

    // Original implementation commented out
    /*
    updateDriverStatusMutation.mutate(newStatus, {
      onSuccess: (data) => {
        console.log('Driver status updated successfully:', data);
        // The driver profile will be automatically refreshed due to query invalidation
        
        // Show success notification
        showNotification({
          title: 'Status Updated',
          body: `Your status has been updated to ${getStatusDisplayName(newStatus)}`,
          playSound: false,
        });
      },
      onError: (error) => {
        console.error('Failed to update driver status:', error);
        showNotification({
          title: 'Status Update Failed',
          body: 'Could not update your status. Please try again.',
          playSound: false,
        });
      }
    });
    */
  };

  // Helper function to get display name for status (supports all status types for display)
  const getStatusDisplayName = (status: string) => {
    switch (status) {
      case 'DISPATCHED':
        return 'Dispatched';
      case 'WAITING':
        return 'Waiting';
      case 'EN_ROUTE':
        return 'En Route';
      case 'ON_SCENE':
        return 'On Scene';
      case 'TOWING':
        return 'Towing';
      case 'RETURNING':
        return 'Returning';
      case 'OFFLINE':
        return 'Offline';
      // Legacy statuses (for backward compatibility with existing data)
      case 'ACTIVE':
        return 'Active';
      case 'CLEAR':
        return 'Clear';
      case 'ON_LEAVE':
        return 'On Leave';
      case 'INVITED':
        return 'Invited';
      default:
        return status;
    }
  };

  const handleCallStatusChange = (callId: string, newStatus: any) => {
    console.log('Call status change:', callId, newStatus);
    // Temporarily using direct action instead of mutation
    actions.updateCallStatus(callId, newStatus);
  };

  const handleCallClick = (callId: string) => {
    console.log('Call clicked:', callId);
    const call = calls.find((c: Call) => c.id === callId);
    if (call) {
      setSelectedCall(call);
      setCallOriginTab('calls'); // Track that we came from calls tab
      // Switch to calls tab to show the CallScreen
      setActiveTab('calls');
    }
  };

  const handleHomeTabCallClick = (callId: string) => {
    console.log('Home tab call clicked:', callId);
    // Find the dispatch and convert to Call format, then open the CallScreen.
    // Stay on the home tab: an office member who does not drive has no
    // Calls tab, so switching to it would be undone by the tabs effect
    // above and the click would do nothing.
    const dispatch = companyDispatches.find((d: any) => d.id === callId);
    if (dispatch) {
      setSelectedCall(dispatch as any); // Cast to any as DriverCall is compatible with Call
      setCallOriginTab('home'); // Track that we came from home tab
    }
  };

  const handleBackFromCall = () => {
    setSelectedCall(null);
    // Return to the tab we came from
    if (callOriginTab) {
      setActiveTab(callOriginTab);
      setCallOriginTab(null);
    }
  };

  const handleTaskComplete = (taskId: string) => {
    actions.completeTask(taskId);
  };

  const handleTaskClick = (taskId: string) => {
    console.log('Task clicked:', taskId);
    // Navigate to task details
  };

  const handleTabChange = useCallback(
    (tabId: string) => {
      console.log('Tab changed to:', tabId);
      setActiveTab(tabId as TabType);
      // Close any open filter popup when switching tabs
      setShowFilterPopup(false);
      setShowSortPopup(false);
      setShowInvoiceSortPopup(false);
      // Clear QR result when switching away from scanner tab
      if (activeTab === 'scanner' && tabId !== 'scanner') {
        setQrResult('');
      }
    },
    [activeTab]
  );

  const handleDriverClick = useCallback(
    (driverId: string, driverName: string) => {
      console.log('Driver clicked:', driverId, driverName);
      setSelectedDriver({ id: driverId, name: driverName });
    },
    []
  );

  const handleBackFromDriverPerformance = useCallback(() => {
    setSelectedDriver(null);
  }, []);

  // Expose embedded mode APIs to window object
  useEffect(() => {
    if (isEmbedded) {
      // Only expose in embedded mode
      console.log('Setting up embedded mode APIs');

      // Create a simple test function first
      (window as any).testEmbeddedAPI = function () {
        return 'Embedded API is working!';
      };

      // Add a debug function to check what's available
      (window as any).debugEmbeddedAPI = function () {
        return {
          hasChangeTab: typeof (window as any).changeTab === 'function',
          hasGetCurrentTab: typeof (window as any).getCurrentTab === 'function',
          hasOpenSettings: typeof (window as any).openSettings === 'function',
          hasTestAPI: typeof (window as any).testEmbeddedAPI === 'function',
          hasSignOut: typeof (window as any).signOut === 'function',
          hasDisableEmbeddedMode:
            typeof (window as any).disableEmbeddedMode === 'function',
          hasEnableEmbeddedMode:
            typeof (window as any).enableEmbeddedMode === 'function',
          hasGetEmbeddedModeInfo:
            typeof (window as any).getEmbeddedModeInfo === 'function',
          showHeader: showHeader,
          showMenu: showMenu,
          effectiveShowMenu: effectiveShowMenu,
          isEmbedded: isEmbedded,
          activeTab: activeTab,
          hasUser: !!user,
          hasDriver: !!driver,
          embeddedModeInfo: getEmbeddedModeDebugInfo(),
        };
      };

      // Create changeTab function
      (window as any).changeTab = function (tabId: string) {
        console.log('Programmatic tab change to:', tabId);

        // Validate tab ID
        const validTabs = [
          'home',
          'calls',
          'impounds',
          'invoices',
          'scanner',
          'drivers',
          'analytics',
        ];
        if (!validTabs.includes(tabId)) {
          console.warn('Invalid tab ID:', tabId, 'Valid tabs:', validTabs);
          return;
        }

        // Use the callback function
        handleTabChange(tabId);
      };

      // Create getCurrentTab function
      (window as any).getCurrentTab = function () {
        return activeTab;
      };

      // getUserInfo function removed for performance optimization

      // Create openSettings function
      (window as any).openSettings = function () {
        console.log('openSettings() called from embedded mode');

        // Call the Android JavaScript interface
        if (
          typeof (window as any).ReactNativeWebView !== 'undefined' &&
          typeof (window as any).ReactNativeWebView.openSettings === 'function'
        ) {
          console.log('Calling ReactNativeWebView.openSettings()');
          (window as any).ReactNativeWebView.openSettings();
        } else {
          console.warn('ReactNativeWebView.openSettings not available');
          // Fallback: dispatch a custom event
          window.dispatchEvent(
            new CustomEvent('openSettings', {
              detail: {
                timestamp: new Date().toISOString(),
              },
            })
          );
        }
      };

      // Create getCurrentLocation function that uses Android location service
      (window as any).getCurrentLocation = function () {
        console.log('getCurrentLocation() called from embedded mode');

        // Call the Android JavaScript interface
        if (
          typeof (window as any).ReactNativeWebView !== 'undefined' &&
          typeof (window as any).ReactNativeWebView.getCurrentLocation ===
            'function'
        ) {
          console.log('Calling ReactNativeWebView.getCurrentLocation()');
          (window as any).ReactNativeWebView.getCurrentLocation();
        } else {
          console.warn('ReactNativeWebView.getCurrentLocation not available');
          // Fallback to browser geolocation
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const locationData = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                  timestamp: position.timestamp,
                  altitude: position.coords.altitude,
                  speed: position.coords.speed,
                  bearing: position.coords.heading,
                };
                if (typeof (window as any).onLocationReceived === 'function') {
                  (window as any).onLocationReceived(locationData);
                }
              },
              (error) => {
                if (typeof (window as any).onLocationError === 'function') {
                  (window as any).onLocationError(error.message);
                }
              },
              {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000,
              }
            );
          } else {
            if (typeof (window as any).onLocationError === 'function') {
              (window as any).onLocationError('Geolocation not supported');
            }
          }
        }
      };

      // Create openScanner function that uses Android QR scanner
      (window as any).openScanner = function () {
        console.log('openScanner() called from embedded mode');

        // Call the Android JavaScript interface
        if (
          typeof (window as any).ReactNativeWebView !== 'undefined' &&
          typeof (window as any).ReactNativeWebView.openScanner === 'function'
        ) {
          console.log('Calling ReactNativeWebView.openScanner()');
          (window as any).ReactNativeWebView.openScanner();
        } else {
          console.warn('ReactNativeWebView.openScanner not available');
          // Fallback: dispatch a custom event
          window.dispatchEvent(
            new CustomEvent('openScanner', {
              detail: {
                timestamp: new Date().toISOString(),
              },
            })
          );
        }
      };

      // Create capturePhoto function that uses Android camera
      (window as any).capturePhoto = function () {
        console.log('capturePhoto() called from embedded mode');

        // Call the Android JavaScript interface
        if (
          typeof (window as any).ReactNativeWebView !== 'undefined' &&
          typeof (window as any).ReactNativeWebView.capturePhoto === 'function'
        ) {
          console.log('Calling ReactNativeWebView.capturePhoto()');
          (window as any).ReactNativeWebView.capturePhoto();
        } else {
          console.warn('ReactNativeWebView.capturePhoto not available');
          // Fallback: dispatch a custom event
          window.dispatchEvent(
            new CustomEvent('capturePhoto', {
              detail: {
                timestamp: new Date().toISOString(),
              },
            })
          );
        }
      };

      // Create explicit handleQrScan function for WebView calls
      (window as any).handleQrScan = function (qrCode: string) {
        console.log('📱 [Explicit QR] handleQrScan called with:', qrCode);
        console.log('📱 [Explicit QR] Current active tab:', activeTab);
        console.log('📱 [Explicit QR] Impound data available:', !!impoundData);
        console.log(
          '📱 [Explicit QR] Impound data length:',
          impoundData?.data?.length || 0
        );
        console.log('📱 [Explicit QR] Is impounds loading:', isImpoundsLoading);

        // Check if impound data is ready
        if (
          !isImpoundsLoading &&
          impoundData?.data &&
          impoundData.data.length > 0
        ) {
          // Data is ready, process immediately
          console.log(
            '📱 [Explicit QR] Data ready, processing QR code immediately'
          );
          handleQrScan([{ rawValue: qrCode }]);
        } else {
          // Data not ready, store as pending
          console.log(
            '📱 [Explicit QR] Data not ready, storing QR code as pending'
          );
          setPendingQrCode(qrCode);
        }
      };

      // Create signOut function that calls the web app's sign out
      (window as any).signOut = function () {
        console.log('📱 [Sign Out] signOut called from native interface');

        // Call the web app's sign out function
        handleSignOut();
      };

      // Create function to explicitly disable embedded mode
      (window as any).disableEmbeddedMode = function () {
        console.log('📱 [Embedded Mode] Explicitly disabling embedded mode');
        setEmbeddedMode(false);
        // Reload the page to apply the change
        window.location.reload();
      };

      // Create function to explicitly enable embedded mode
      (window as any).enableEmbeddedMode = function () {
        console.log('📱 [Embedded Mode] Explicitly enabling embedded mode');
        setEmbeddedMode(true);
        // Reload the page to apply the change
        window.location.reload();
      };

      // Create function to get embedded mode debug info
      (window as any).getEmbeddedModeInfo = function () {
        return getEmbeddedModeDebugInfo();
      };

      // Create navigation functions for Android swipe gestures
      (window as any).navigateBack = function () {
        console.log('📱 [Navigation] navigateBack called from Android');

        // If we're on a sub-screen, go back to parent
        if (selectedCall) {
          console.log(
            '📱 [Navigation] Going back from CallScreen to HomeScreen'
          );
          handleBackFromCall();
          return;
        }

        if (selectedImpound) {
          console.log(
            '📱 [Navigation] Going back from ImpoundScreen to HomeScreen'
          );
          handleBackFromImpound();
          return;
        }

        // If we're on HomeScreen, navigate to previous tab
        const tabOrder: readonly string[] = tabs;
        const currentIndex = tabOrder.indexOf(activeTab);

        if (currentIndex > 0) {
          const previousTab = tabOrder[currentIndex - 1];
          console.log(
            '📱 [Navigation] Moving from',
            activeTab,
            'to',
            previousTab
          );
          handleTabChange(previousTab);
        } else {
          console.log('📱 [Navigation] Already at first tab:', activeTab);
        }
      };

      (window as any).navigateForward = function () {
        console.log('📱 [Navigation] navigateForward called from Android');

        // If we're on a sub-screen, do nothing (forward navigation not applicable)
        if (selectedCall || selectedImpound) {
          console.log(
            '📱 [Navigation] Forward navigation not applicable on sub-screens'
          );
          return;
        }

        // If we're on HomeScreen, navigate to next tab
        const tabOrder: readonly string[] = tabs;
        const currentIndex = tabOrder.indexOf(activeTab);

        if (currentIndex < tabOrder.length - 1) {
          const nextTab = tabOrder[currentIndex + 1];
          console.log('📱 [Navigation] Moving from', activeTab, 'to', nextTab);
          handleTabChange(nextTab);
        } else {
          console.log('📱 [Navigation] Already at last tab:', activeTab);
        }
      };

      console.log('Embedded mode APIs set up successfully');

      // Cleanup function
      return () => {
        console.log('Cleaning up embedded mode APIs');
        delete (window as any).changeTab;
        delete (window as any).getCurrentTab;
        delete (window as any).openSettings;
        delete (window as any).getCurrentLocation;
        delete (window as any).openScanner;
        delete (window as any).capturePhoto;
        delete (window as any).handleQrScan;
        delete (window as any).signOut;
        delete (window as any).disableEmbeddedMode;
        delete (window as any).enableEmbeddedMode;
        delete (window as any).getEmbeddedModeInfo;
        delete (window as any).navigateBack;
        delete (window as any).navigateForward;
        delete (window as any).testEmbeddedAPI;
        delete (window as any).debugEmbeddedAPI;
      };
    }
  }, [
    isEmbedded,
    activeTab,
    showSettings,
    user,
    driver,
    handleTabChange,
    selectedCall,
    selectedImpound,
    tabs,
  ]);

  const handleFilterClick = () => {
    setShowFilterPopup(!showFilterPopup);
  };

  const handleFilterSelect = (option: FilterOption) => {
    setFilterOption(option);
    setShowFilterPopup(false);
  };

  const handleInvoiceClick = (dispatchId: string) => {
    console.log('Invoice clicked, navigating to payment screen:', dispatchId);
    setSelectedPaymentDispatch(dispatchId);
  };

  const handleCloseSettings = () => {
    setShowSettingsOverlay(false);
  };

  const handleSortClick = () => {
    setShowSortPopup(!showSortPopup);
  };

  const handleSortSelect = (option: SortOption) => {
    setSortOption(option);
    setShowSortPopup(false);
  };

  const handleInvoiceSortClick = () => {
    setShowInvoiceSortPopup(!showInvoiceSortPopup);
  };

  const handleInvoiceSortSelect = (option: 'Latest' | 'Oldest') => {
    setInvoiceSortOption(option);
    setShowInvoiceSortPopup(false);
  };

  const handleImpoundClick = (impoundId: string) => {
    console.log('Impound clicked:', impoundId);

    // Fire-and-forget prefetch so ImpoundScreen loads from cache on mount
    const profile = queryClient.getQueryData<{ companyId: string }>([
      'driver-profile',
    ]);
    if (profile?.companyId) {
      getAccessTokenSilently().then((token) => {
        prefetchDriverImpound(queryClient, token, impoundId, profile.companyId);

        // Optionally prefetch vehicle images if vehicle id is available from the list cache
        const impound = impoundData?.data?.find((i) => i.id === impoundId);
        const vehicleId = impound?.dispatch?.vehicle?.id;
        if (vehicleId) {
          prefetchVehicleImages(queryClient, token, vehicleId);
        }
      });
    }

    setSelectedImpound(impoundId);
  };

  const handleBackFromImpound = () => {
    setSelectedImpound(null);
  };

  const handleViewAllCalls = () => {
    setShowAllCalls(true);
  };

  const handleBackFromAllCalls = () => {
    setShowAllCalls(false);
  };

  const handleViewAllTasks = () => {
    setShowAllTasks(true);
  };

  const handleBackFromAllTasks = () => {
    setShowAllTasks(false);
  };

  const handleShowCreateCall = () => {
    setShowCreateCall(true);
  };

  const handleBackFromCreateCall = () => {
    setShowCreateCall(false);
  };

  const handleCreateCallSuccess = () => {
    console.log('Call created successfully, navigating to drivers tab');
    setShowCreateCall(false);
    setActiveTab('drivers');
  };

  const handleTakePhoto = () => {
    const startTime = performance.now();
    // Check if we're in embedded mode (Android app)
    const isEmbeddedMode = isEmbedded;

    if (isEmbeddedMode && typeof (window as any).openScanner === 'function') {
      console.log('Using native Android QR scanner');
      setIsScanning(true);
      setQrResult(''); // Clear previous results

      // Call the native Android QR scanner
      (window as any).openScanner();
      const duration = performance.now() - startTime;
      console.log(`QR scanner launch took ${duration.toFixed(2)}ms`);
      return;
    }

    // Fallback to web-based scanning
    setIsScanning(true);
    setQrResult(''); // Clear previous results
    const duration = performance.now() - startTime;
    console.log(`Web scanner setup took ${duration.toFixed(2)}ms`);
  };

  const handleQrScan = (result: any) => {
    if (result && result.length > 0) {
      const scannedData = result[0].rawValue;
      setQrResult(scannedData);
      setIsScanning(false);

      // Get all impounds (unfiltered) for QR code matching
      const allImpounds = impoundData?.data
        ? impoundData.data.map(mapDriverImpoundToImpound)
        : [];

      // Enhanced debugging for QR code matching
      console.log('🔍 [QR Scan] Scanned data:', scannedData);
      console.log('🔍 [QR Scan] Impound data available:', !!impoundData);
      console.log(
        '🔍 [QR Scan] Impound data length:',
        impoundData?.data?.length || 0
      );
      console.log('🔍 [QR Scan] Is impounds loading:', isImpoundsLoading);
      console.log(
        '🔍 [QR Scan] All available impounds:',
        allImpounds.map((imp) => ({
          id: imp.id,
          impoundNumber: imp.impoundNumber,
          status: imp.status,
        }))
      );
      console.log('🔍 [QR Scan] Current filter setting:', sortOption);
      console.log('🔍 [QR Scan] Current active tab:', activeTab);

      // If impound data is still loading, log and return early
      if (isImpoundsLoading || allImpounds.length === 0) {
        console.log(
          '⏳ [QR Scan] Impound data not ready yet. Data will be processed when available.'
        );
        console.log(
          '⏳ [QR Scan] Use window.handleQrScan(qrCode) to retry when data is loaded.'
        );
        return;
      }

      // Multiple search strategies - try each one before giving up
      let matchedImpound = null;
      let searchAttempts = 0;

      if (allImpounds.length > 0) {
        console.log('🔍 [QR Scan] Starting comprehensive search...');

        // Strategy 1: Exact ID match
        searchAttempts++;
        console.log(`🔍 [QR Scan] Strategy ${searchAttempts}: Exact ID match`);
        matchedImpound = allImpounds.find((impound) => {
          if (impound.id === scannedData) {
            console.log(
              '✅ [QR Scan] Exact ID match found:',
              impound.impoundNumber
            );
            return true;
          }
          return false;
        });

        // Strategy 2: Exact impound number match
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Exact impound number match`
          );
          matchedImpound = allImpounds.find((impound) => {
            if (impound.impoundNumber === scannedData) {
              console.log(
                '✅ [QR Scan] Exact impound number match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        // Strategy 3: Case-insensitive ID match
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Case-insensitive ID match`
          );
          matchedImpound = allImpounds.find((impound) => {
            if (impound.id.toLowerCase() === scannedData.toLowerCase()) {
              console.log(
                '✅ [QR Scan] Case-insensitive ID match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        // Strategy 4: Case-insensitive impound number match
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Case-insensitive impound number match`
          );
          matchedImpound = allImpounds.find((impound) => {
            if (
              impound.impoundNumber.toLowerCase() === scannedData.toLowerCase()
            ) {
              console.log(
                '✅ [QR Scan] Case-insensitive impound number match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        // Strategy 5: Partial ID match (contains)
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Partial ID match (contains)`
          );
          matchedImpound = allImpounds.find((impound) => {
            if (
              scannedData.includes(impound.id) ||
              impound.id.includes(scannedData)
            ) {
              console.log(
                '✅ [QR Scan] Partial ID match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        // Strategy 6: Partial impound number match (contains)
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Partial impound number match (contains)`
          );
          matchedImpound = allImpounds.find((impound) => {
            if (
              scannedData.includes(impound.impoundNumber) ||
              impound.impoundNumber.includes(scannedData)
            ) {
              console.log(
                '✅ [QR Scan] Partial impound number match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        // Strategy 7: Trimmed whitespace match
        if (!matchedImpound) {
          searchAttempts++;
          console.log(
            `🔍 [QR Scan] Strategy ${searchAttempts}: Trimmed whitespace match`
          );
          const trimmedScannedData = scannedData.trim();
          matchedImpound = allImpounds.find((impound) => {
            if (
              impound.id.trim() === trimmedScannedData ||
              impound.impoundNumber.trim() === trimmedScannedData
            ) {
              console.log(
                '✅ [QR Scan] Trimmed whitespace match found:',
                impound.impoundNumber
              );
              return true;
            }
            return false;
          });
        }

        console.log(
          `🔍 [QR Scan] Completed ${searchAttempts} search strategies`
        );
      }

      if (matchedImpound) {
        // Switch to impounds tab and open the matched impound
        console.log('✅ [QR Scan] Successfully matched impound:', {
          id: matchedImpound.id,
          impoundNumber: matchedImpound.impoundNumber,
          status: matchedImpound.status,
        });

        // Show success feedback briefly before navigating
        console.log(
          '🔄 [QR Scan] About to navigate to impounds tab for impound:',
          matchedImpound.id
        );
        setTimeout(() => {
          console.log('🔄 [QR Scan] Setting active tab to impounds');
          setActiveTab('impounds');
          console.log(
            '🔄 [QR Scan] Setting selected impound to:',
            matchedImpound.id
          );
          setSelectedImpound(matchedImpound.id);
          // Clear QR result after successful navigation
          setTimeout(() => {
            console.log('🔄 [QR Scan] Clearing QR result');
            setQrResult('');
          }, 500);
        }, 500); // Reduced delay from 1500ms to 500ms
      } else {
        console.log(
          '❌ [QR Scan] ALL SEARCH STRATEGIES EXHAUSTED - No matching impound found'
        );
        console.log(
          '❌ [QR Scan] Tried',
          searchAttempts,
          'different search strategies for QR code:',
          scannedData
        );
        console.log('❌ [QR Scan] This could be because:');
        console.log(
          "  - The impound is not in the current driver's impound list"
        );
        console.log('  - The QR code contains unexpected data');
        console.log("  - The impound data hasn't loaded yet");
        console.log('  - The impound belongs to a different company');
        console.log('  - The impound has been deleted or archived');
        console.log('  - The QR code format is not recognized');
      }
    }
  };

  const handleQrError = (error: any) => {
    console.error('QR Scanner error:', error);
    setIsScanning(false);
  };

  const stopScanning = () => {
    setIsScanning(false);
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const filterButton = filterButtonRef.current;
      const sortButton = sortButtonRef.current;
      const invoiceSortButton = invoiceSortButtonRef.current;
      const driversSortButton = driversSortButtonRef.current;
      const filterPopup = document.querySelector('[data-filter-popup]');
      const sortPopup = document.querySelector('[data-sort-popup]');
      const driversSortPopup = document.querySelector(
        '[data-drivers-sort-popup]'
      );

      if (
        filterButton &&
        !filterButton.contains(target) &&
        filterPopup &&
        !filterPopup.contains(target)
      ) {
        setShowFilterPopup(false);
      }

      if (
        sortButton &&
        !sortButton.contains(target) &&
        sortPopup &&
        !sortPopup.contains(target)
      ) {
        setShowSortPopup(false);
      }

      if (
        invoiceSortButton &&
        !invoiceSortButton.contains(target) &&
        sortPopup &&
        !sortPopup.contains(target)
      ) {
        setShowInvoiceSortPopup(false);
      }

      if (
        driversSortButton &&
        !driversSortButton.contains(target) &&
        driversSortPopup &&
        !driversSortPopup.contains(target)
      ) {
        setShowDriversSortPopup(false);
      }
    };

    if (
      showFilterPopup ||
      showSortPopup ||
      showInvoiceSortPopup ||
      showDriversSortPopup
    ) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    showFilterPopup,
    showSortPopup,
    showInvoiceSortPopup,
    showDriversSortPopup,
  ]);

  const retryLocation = () => {
    actions.requestLocation();
  };

  const retryDataLoad = () => {
    actions.refreshData();
  };

  const formatVehicleDescription = (
    year: number,
    make: string,
    model: string
  ) => {
    console.log('🏠 [HomeScreen] Formatting vehicle description:', {
      year,
      make,
      model,
    });
    // If both make and model are not provided, show "Unspecified Vehicle"
    if (
      (!make || make == 'null' || make == 'Unknown') &&
      (!model || model == 'null' || model == 'Unknown')
    ) {
      console.log('🏠 [HomeScreen] Unspecified Vehicle');
      return 'Unspecified Vehicle';
    }

    // Build the description parts
    const parts: string[] = [];

    // Add year only if it's valid (not 0, null, or blank)
    console.log('🏠 [HomeScreen] Year:', year);
    if (year && year > 0) {
      parts.push(year.toString());
      console.log('🏠 [HomeScreen] Year added:', year.toString());
    }

    // Add make if provided
    if (make && make !== 'null' && make !== 'unknown' && make !== 'Unknown') {
      console.log('🏠 [HomeScreen] Make added:', make);
      parts.push(make);
    }

    // Add model if provided
    if (
      model &&
      model !== 'null' &&
      model !== 'unknown' &&
      model !== 'Unknown'
    ) {
      console.log('🏠 [HomeScreen] Model added:', model);
      parts.push(model);
    }

    const result = parts.join(' ');
    console.log('🏠 [HomeScreen] Vehicle description result:', result);
    console.log(
      '🏠 [HomeScreen] Vehicle description result type:',
      typeof result
    );
    return result;
  };

  // New call popup handlers - temporarily disabled
  // const handleStartCall = async () => {
  //   // Since newCall is set to null, this function won't do anything
  //   if (!driver) return;
  //   console.log('Start call handler - currently disabled');
  //
  //   // Clear the popup just in case
  //   clearNewCall();
  // };
  //
  // const handleDismissNewCall = () => {
  //   clearNewCall();
  // };

  const handleSignOut = () => {
    console.log('📱 [Sign Out] handleSignOut called');

    // Check if we're in embedded mode (in native app WebView)
    const isEmbeddedMode = window.location.search.includes('embedded=true');

    if (isEmbeddedMode) {
      console.log(
        '📱 [Sign Out] Embedded mode detected - using native signOut interface'
      );

      // Try to use the native interface to tell the app to show the sign-in screen
      try {
        // For iOS WebView
        if (
          (window as any).webkit &&
          (window as any).webkit.messageHandlers &&
          (window as any).webkit.messageHandlers.iosApp
        ) {
          console.log(
            '📱 [Sign Out] iOS WebView detected - sending signOut message'
          );
          (window as any).webkit.messageHandlers.iosApp.postMessage({
            type: 'signOut',
          });
          return; // Exit early since we're handling via native interface
        }

        // For Android WebView
        if ((window as any).ReactNativeWebView) {
          console.log(
            '📱 [Sign Out] Android/React Native WebView detected - sending signOut message'
          );
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify({
              type: 'signOut',
            })
          );
          return; // Exit early since we're handling via native interface
        }
      } catch (error) {
        console.error(
          '📱 [Sign Out] Error using native signOut interface:',
          error
        );
      }
    }

    // Fallback to standard Auth0 logout if not in embedded mode or native interface failed
    console.log('📱 [Sign Out] Using standard Auth0 logout');
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  // Data loading is now handled in the splash screen, so this loading state is no longer needed
  // All error states are now handled as non-blocking banners within the HomeScreen UI

  // A dead session beats every other screen here, but only when there is
  // nothing to show. A failed background refetch while cached data is still on
  // screen stays a banner, so a driver mid job is not thrown out. Same rule as
  // fallbackMode in useDriverStateV2. A calls or tasks failure is always a
  // banner, because the rest of the app still works.
  if (sessionError !== null && driver === null) {
    return (
      <AuthErrorScreen
        kind={sessionError}
        errorMessage={dataError ?? undefined}
      />
    );
  }

  // Show CreateDriverScreen if creating a new driver (the Dispatch tab)
  if (showCreateDriver && hasDriversScreen) {
    return (
      <CreateDriverScreen
        onBack={() => setShowCreateDriver(false)}
        showHeader={showHeader}
      />
    );
  }

  // Show CreateCallScreen if creating a new call (the Home tab)
  if (showCreateCall && hasHomeScreen) {
    return (
      <CreateCallScreen
        onBack={handleBackFromCreateCall}
        onSuccess={handleCreateCallSuccess}
        showHeader={showHeader}
      />
    );
  }

  // Show AllCallsScreen if viewing all calls
  if (showAllCalls) {
    return (
      <AllCallsScreen
        calls={calls}
        onBack={handleBackFromAllCalls}
        onCallClick={handleCallClick}
        onStatusChange={handleCallStatusChange}
        onNavigateToPayment={(dispatchId) => {
          setSelectedPaymentDispatch(dispatchId);
        }}
        onImpoundCreated={(impoundId) => {
          setShowAllCalls(false);
          setActiveTab('impounds');
          setSelectedImpound(impoundId);
        }}
        showHeader={showHeader}
      />
    );
  }

  // Show AllTasksScreen if viewing all tasks
  if (showAllTasks) {
    return (
      <AllTasksScreen
        tasks={tasks}
        onBack={handleBackFromAllTasks}
        onTaskComplete={handleTaskComplete}
        onTaskClick={handleTaskClick}
        showHeader={showHeader}
      />
    );
  }

  // Show PaymentScreen if a payment dispatch is selected
  if (selectedPaymentDispatch) {
    return (
      <PaymentScreen
        dispatchId={selectedPaymentDispatch}
        onBack={() => {
          setSelectedPaymentDispatch(null);
          // Return to the call if it was selected
          if (selectedCall) {
            setSelectedCall(selectedCall);
          }
        }}
      />
    );
  }

  // Show CallScreen if a call is selected (from the calls or home tab)
  if (selectedCall && (activeTab === 'calls' || activeTab === 'home')) {
    return (
      <CallScreen
        callId={selectedCall.id}
        onBack={handleBackFromCall}
        onImpoundCreated={(impoundId) => {
          console.log(
            'Impound created, navigating to impound screen:',
            impoundId
          );
          setActiveTab('impounds');
          setSelectedImpound(impoundId);
        }}
        onNavigateToPayment={(dispatchId) => {
          setSelectedPaymentDispatch(dispatchId);
        }}
        showHeader={showHeader}
      />
    );
  }

  // Show ImpoundScreen if an impound is selected (only on impounds tab)
  if (selectedImpound && activeTab === 'impounds') {
    return (
      <ImpoundScreen
        impoundId={selectedImpound}
        onBack={handleBackFromImpound}
        showHeader={showHeader}
      />
    );
  }

  // Process impounds based on selected option and current driver
  const getSortedImpounds = () => {
    console.log('🏠 [HomeScreen] Processing impounds:', {
      hasImpoundData: !!impoundData,
      impoundDataLength: impoundData?.data?.length || 0,
      sortOption,
      driverId: driver?.id,
      driverName: getDriverDisplayName(driver, 'No driver'),
    });

    if (!impoundData?.data) return [];

    // Convert backend impounds to frontend format
    let impounds = impoundData.data.map(mapDriverImpoundToImpound);

    console.log(
      '🏠 [HomeScreen] Mapped impounds:',
      impounds.map((imp) => ({
        id: imp.id,
        impoundNumber: imp.impoundNumber,
        driverId: imp.driverId,
        companyId: imp.companyId,
        vehicleInfo: formatVehicleDescription(
          imp.vehicleYear,
          imp.vehicleMake,
          imp.vehicleModel
        ),
        status: imp.status,
      }))
    );

    // No filtering here: GET /impounds returns only what the caller's role
    // may see (impounds.view_all / view_own).

    // Add completedByCurrentDriver logic based on current driver ID
    if (driver) {
      impounds = impounds.map((impound) => ({
        ...impound,
        completedByCurrentDriver: impound.driverId === driver.id,
      }));

      console.log(
        '🏠 [HomeScreen] After adding completedByCurrentDriver flag:',
        impounds.map((imp) => ({
          id: imp.id,
          driverId: imp.driverId,
          currentDriverId: driver.id,
          completedByCurrentDriver: imp.completedByCurrentDriver,
        }))
      );
    }

    // Filter for "Completed by you" option
    if (sortOption === 'Completed by you') {
      const filteredImpounds = impounds.filter(
        (impound) => impound.completedByCurrentDriver
      );
      console.log(
        '🏠 [HomeScreen] Filtered for "Completed by you":',
        filteredImpounds.length
      );
      impounds = filteredImpounds;
    }

    console.log('🏠 [HomeScreen] Final impounds to render:', impounds.length);
    return impounds;
  };

  // Filter impounds by search query (for owner users)
  const getFilteredImpounds = () => {
    const impounds = getSortedImpounds();

    if (!impoundsSearchQuery.trim()) {
      return impounds;
    }

    const query = impoundsSearchQuery.toLowerCase();
    return impounds.filter(
      (impound) =>
        impound.impoundNumber?.toLowerCase().includes(query) ||
        impound.id?.toLowerCase().includes(query) ||
        impound.vehicleMake?.toLowerCase().includes(query) ||
        impound.vehicleModel?.toLowerCase().includes(query) ||
        impound.vehicleYear?.toString().includes(query) ||
        impound.licensePlate?.toLowerCase().includes(query)
    );
  };

  const getDisplayError = () => {
    // An expired token used to be relabelled "No company found", which sent
    // drivers to their admin over a session they could have fixed by signing
    // in again. Say which failure it actually is.
    if (authError === 'session-expired') return 'Your session expired';
    if (!dataError) return null;
    return dataError;
  };

  const renderTabContent = () => {
    const displayError = getDisplayError();

    return (
      <>
        {/* Data Error Banner - Non-blocking */}
        {displayError && (
          <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50 mx-4 mt-4">
            <div className="flex items-center text-red-600">
              <AlertCircle size={16} className="mr-2 flex-shrink-0" />
              <span className="text-sm">{displayError}</span>
            </div>
          </div>
        )}

        {/* Tab Content */}
        {(() => {
          switch (activeTab) {
            case 'home':
              if (!hasHomeScreen) return null;

              return (
                <div className="px-4 py-4 space-y-6">
                  {(() => {
                    // Get today's date range
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const endOfToday = new Date(today);
                    endOfToday.setHours(23, 59, 59, 999);

                    // Daily Summary calculations
                    const todaysDispatches = companyDispatches.filter((d) => {
                      const created = new Date(d.createdAt);
                      return created >= today && created <= endOfToday;
                    });
                    const totalCallsToday = todaysDispatches.length;
                    const completedCallsToday = todaysDispatches.filter(
                      (d) =>
                        d.status === 'COMPLETED' ||
                        d.status === 'COMPLETED_AWAITING'
                    ).length;
                    const activeDriversCount = companyDrivers.filter(
                      (d) => d.status === 'ACTIVE'
                    ).length;

                    // Calls Card calculations
                    const pendingCallsCount = companyDispatches.filter(
                      (d) => d.status === 'NEW' || d.status === 'PENDING'
                    ).length;
                    const activeCallsCount = companyDispatches.filter((d) =>
                      ['DISPATCHED', 'ON_SCENE', 'TOWING'].includes(d.status)
                    ).length;

                    // Impounds Card calculations
                    const impounds = impoundData?.data || [];
                    const currentImpoundsCount = impounds.filter(
                      (i) => i.status === 'ACTIVE'
                    ).length;
                    const newImpoundsToday = impounds.filter((i) => {
                      const impoundDate = new Date(i.impoundDate);
                      return impoundDate >= today && impoundDate <= endOfToday;
                    }).length;

                    return (
                      <>
                        {/* Daily Summary Card */}
                        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-black">
                              Daily Summary
                            </h3>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-3 gap-4">
                              {/* Total Calls */}
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <div className="text-3xl font-bold text-purple-600">
                                  {totalCallsToday}
                                </div>
                                <div className="text-xs text-purple-700 mt-1">
                                  Total Calls
                                </div>
                              </div>
                              {/* Completed Calls */}
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div
                                  className="text-3xl font-bold"
                                  style={{ color: '#45A17D' }}
                                >
                                  {completedCallsToday}
                                </div>
                                <div
                                  className="text-xs mt-1"
                                  style={{ color: '#45A17D' }}
                                >
                                  Completed
                                </div>
                              </div>
                              {/* Active Drivers */}
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="text-3xl font-bold text-blue-600">
                                  {activeDriversCount}
                                </div>
                                <div className="text-xs text-blue-700 mt-1">
                                  Active Drivers
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Calls Section */}
                        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <ActiveServiceCallsTable
                            dispatches={companyDispatches}
                            customers={customersData?.data}
                            showHeader={true}
                            onCallClick={handleHomeTabCallClick}
                            showViewAllButton={true}
                            onViewAllClick={() => handleTabChange('drivers')}
                            showFilterButton={true}
                            maxRows={4}
                          />
                        </div>

                        {/* Impounds Card */}
                        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-black">
                              Impounds
                            </h3>
                          </div>
                          <div className="p-4">
                            <div className="grid grid-cols-3 gap-4">
                              {/* Current Impounds */}
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                <div className="text-3xl font-bold text-purple-600">
                                  {currentImpoundsCount}
                                </div>
                                <div className="text-xs text-purple-700 mt-1">
                                  Current Impounds
                                </div>
                              </div>
                              {/* New Impounds */}
                              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div
                                  className="text-3xl font-bold"
                                  style={{ color: '#45A17D' }}
                                >
                                  {newImpoundsToday}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  New Impounds
                                </div>
                              </div>
                              {/* Vehicles Released */}
                              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="text-3xl font-bold text-orange-600">
                                  {releasedImpoundsCount}
                                </div>
                                <div className="text-xs text-orange-700 mt-1">
                                  Vehicles Released
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Drivers Section */}
                        <div className="bg-background border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-black">
                              Drivers
                            </h3>
                            <div className="relative">
                              <button
                                ref={driversSortButtonRef}
                                onClick={() =>
                                  setShowDriversSortPopup(!showDriversSortPopup)
                                }
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <ArrowUpDown
                                  size={20}
                                  className="text-gray-600"
                                />
                              </button>

                              {/* Sort Popup */}
                              {showDriversSortPopup && (
                                <div
                                  data-drivers-sort-popup
                                  className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[120px]"
                                >
                                  {(['A-Z', 'Z-A', 'Status'] as const).map(
                                    (option) => (
                                      <button
                                        key={option}
                                        onClick={() => {
                                          setDriversSortOption(option);
                                          setShowDriversSortPopup(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                          driversSortOption === option
                                            ? 'bg-primary/10 text-primary font-medium'
                                            : 'text-gray-700'
                                        }`}
                                      >
                                        {option}
                                      </button>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="divide-y divide-gray-200">
                            {/* Driver rows: whoever drives for the company */}
                            {(() => {
                              // Sort drivers based on selected sort option
                              const driversOnCall = [...companyDrivers].sort(
                                (a, b) => {
                                  if (driversSortOption === 'A-Z') {
                                    return a.name.localeCompare(b.name);
                                  } else if (driversSortOption === 'Z-A') {
                                    return b.name.localeCompare(a.name);
                                  } else if (driversSortOption === 'Status') {
                                    // Sort by status: ACTIVE, DISPATCHED, then others
                                    const statusOrder = {
                                      ACTIVE: 0,
                                      DISPATCHED: 1,
                                      WAITING: 2,
                                      EN_ROUTE: 3,
                                      ON_SCENE: 4,
                                      TOWING: 5,
                                      RETURNING: 6,
                                      CLEAR: 7,
                                      ON_LEAVE: 8,
                                      OFFLINE: 9,
                                    };
                                    const statusA =
                                      statusOrder[
                                        a.status as keyof typeof statusOrder
                                      ] ?? 99;
                                    const statusB =
                                      statusOrder[
                                        b.status as keyof typeof statusOrder
                                      ] ?? 99;
                                    if (statusA !== statusB) {
                                      return statusA - statusB;
                                    }
                                    // If same status, sort by name A-Z
                                    return a.name.localeCompare(b.name);
                                  }
                                  return 0;
                                }
                              );

                              if (driversOnCall.length === 0) {
                                return (
                                  <div className="px-4 py-3 text-center text-sm text-gray-400">
                                    No drivers available
                                  </div>
                                );
                              }

                              return driversOnCall.map((driver) => {
                                // Same status color logic as DriversScreen
                                const statusColor =
                                  driver.status === 'ACTIVE'
                                    ? {
                                        bg: '#dcfce7',
                                        text: '#166534',
                                        label: 'Available',
                                      }
                                    : driver.status === 'DISPATCHED'
                                      ? {
                                          bg: '#fecaca',
                                          text: '#991b1b',
                                          label: 'On Call',
                                        }
                                      : {
                                          bg: '#f3f4f6',
                                          text: '#6b7280',
                                          label: 'Off Duty',
                                        };

                                return (
                                  <div
                                    key={driver.id}
                                    className="px-4 py-3 flex items-center justify-between"
                                  >
                                    <div className="flex-1">
                                      <p className="font-semibold text-black">
                                        {driver.name}
                                      </p>
                                      <p className="text-sm text-gray-600">
                                        Truck #{driver.truckNumber}
                                      </p>
                                    </div>
                                    <span
                                      className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-3"
                                      style={{
                                        backgroundColor: statusColor.bg,
                                        color: statusColor.text,
                                      }}
                                    >
                                      {statusColor.label}
                                    </span>
                                  </div>
                                );
                              });
                            })()}
                            {/* Add Driver Row */}
                            <button
                              onClick={() => setShowCreateDriver(true)}
                              className="w-full px-4 py-3 flex items-center justify-center gap-2 text-black hover:bg-gray-50 transition-colors border-t border-gray-200"
                            >
                              <span className="text-lg">+</span>
                              <span className="text-sm font-medium">
                                Add Driver
                              </span>
                            </button>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              );

            case 'impounds':
              return (
                <div className="px-4 py-4">
                  {/* Title row; search + sort on second row */}
                  <div className="flex flex-col gap-3 mb-4">
                    <h2
                      className="text-2xl font-semibold"
                      style={{ color: '#000000' }}
                    >
                      Impounds
                    </h2>
                    <div
                      className={`flex items-center gap-3 ${hasOfficeRole ? 'justify-between' : 'justify-end'}`}
                    >
                      {/* Search Box - the office side */}
                      {hasOfficeRole && (
                        <div className="flex-1 max-w-xs min-w-0">
                          <div className="relative">
                            <Search
                              size={16}
                              className="absolute left-3 top-1/2 transform -translate-y-1/2"
                              style={{ color: '#9CA3AF' }}
                            />
                            <SearchInputWithCustomKeyboard
                              value={impoundsSearchQuery}
                              onChange={setImpoundsSearchQuery}
                              placeholder="Search impounds..."
                              onClear={() => setImpoundsSearchQuery('')}
                              className="w-full pl-9"
                            />
                          </div>
                        </div>
                      )}
                      <div className="relative flex-shrink-0">
                        <button
                          ref={sortButtonRef}
                          onClick={handleSortClick}
                          className="p-2 rounded-lg transition-colors"
                          style={{
                            backgroundColor: 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F3F4F6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }}
                        >
                          <ArrowUpDown size={20} style={{ color: '#4B5563' }} />
                        </button>

                        {/* Sort Popup */}
                        {showSortPopup && (
                          <div
                            data-sort-popup
                            className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[160px]"
                          >
                            {(
                              [
                                'Latest',
                                'Oldest',
                                'Completed by you',
                              ] as SortOption[]
                            ).map((option) => (
                              <button
                                key={option}
                                onClick={() => handleSortSelect(option)}
                                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                  sortOption === option
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Impounds List - Show empty state even if there's an error loading from impounds API */}
                  <div className="space-y-3">
                    {getFilteredImpounds().length > 0 ? (
                      getFilteredImpounds().map((impound) => (
                        <ImpoundCard
                          key={impound.id}
                          impound={impound}
                          onCardClick={handleImpoundClick}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {sortOption === 'Completed by you' ? (
                          <>
                            <p>No impounds completed by you</p>
                            <p className="text-sm mt-1">
                              Completed impounds will appear here
                            </p>
                          </>
                        ) : (
                          <>
                            <p>No active impounds at the moment</p>
                            <p className="text-sm mt-1">
                              New impounds will appear here
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );

            case 'calls':
              return (
                <div className="px-4 py-4">
                  {/* Data Loading Indicator */}
                  {isDataLoading ? (
                    <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50">
                      <div className="flex items-center text-blue-600">
                        <Loader2 size={16} className="animate-spin mr-2" />
                        <span className="text-sm">Refreshing data...</span>
                        <span className="text-xs ml-2 opacity-75">
                          (Auto-refresh every 30s)
                        </span>
                      </div>
                    </div>
                  ) : null}

                  {/* Data Error Indicator - Hide if it's an authorization error (shown in main banner) */}
                  {dataError &&
                  !dataError.includes('Unauthorized') &&
                  !dataError.includes('401') ? (
                    <div className="mb-4 p-3 rounded-lg border border-red-200 bg-red-50">
                      <div className="flex items-center justify-between text-red-600">
                        <div className="flex items-center">
                          <AlertCircle size={16} className="mr-2" />
                          <span className="text-sm">
                            Failed to refresh data
                          </span>
                        </div>
                        <button
                          onClick={retryDataLoad}
                          className="text-xs bg-red-100 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {/* Location Status */}
                  {isLocationLoading || locationError ? (
                    <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {isLocationLoading && (
                            <div className="flex items-center text-blue-600">
                              <div className="animate-spin mr-2 h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                              <span className="text-sm">
                                Getting your location...
                              </span>
                            </div>
                          )}

                          {locationError && !isLocationLoading && (
                            <div className="flex items-center text-blue-600">
                              <AlertCircle size={16} className="mr-2" />
                              <span className="text-sm">
                                {locationError.message}
                              </span>
                              <button
                                onClick={retryLocation}
                                className="text-xs bg-red-50 px-2 py-1 rounded hover:bg-red-100 transition-colors ml-2"
                              >
                                Retry
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Calls Section */}
                  {(filterOption === 'All' ||
                    filterOption === 'Calls' ||
                    filterOption === 'Recents') && (
                    <div className="mb-6">
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-2xl font-semibold text-black">
                          Calls
                        </h2>
                        <div className="relative">
                          <button
                            ref={filterButtonRef}
                            onClick={handleFilterClick}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Filter
                              size={20}
                              className={`${callsFilterOption === 'Active Only' ? 'text-gray-600' : 'text-primary'}`}
                              fill={
                                callsFilterOption === 'Active Only'
                                  ? 'none'
                                  : 'currentColor'
                              }
                            />
                          </button>

                          {/* Filter Popup */}
                          {showFilterPopup && (
                            <div
                              data-filter-popup
                              className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[160px]"
                            >
                              {(
                                [
                                  'Active Only',
                                  'Show Completed',
                                  'Last 7 Days',
                                ] as CallsFilterOption[]
                              ).map((option) => (
                                <button
                                  key={option}
                                  onClick={() => {
                                    setCallsFilterOption(option);
                                    setShowFilterPopup(false);
                                  }}
                                  className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                    callsFilterOption === option
                                      ? 'bg-primary/10 text-primary font-medium'
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {option}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {(() => {
                          // Filter calls based on selected option
                          const now = new Date();
                          let filteredCalls = calls;

                          if (callsFilterOption === 'Active Only') {
                            // Show only active/incomplete calls
                            filteredCalls = calls.filter(
                              (call) => call.status !== 'complete'
                            );
                          } else if (callsFilterOption === 'Show Completed') {
                            // Show all calls (both active and completed) - no status filtering
                            filteredCalls = calls;
                          } else if (callsFilterOption === 'Last 7 Days') {
                            // Show all calls (both active and completed) from last 7 days
                            const sevenDaysAgo = new Date(
                              now.getTime() - 7 * 24 * 60 * 60 * 1000
                            );
                            filteredCalls = calls.filter((call) => {
                              const callDate = new Date(call.dispatchedTime);
                              return (
                                callDate >= sevenDaysAgo && callDate <= now
                              );
                            });
                          }

                          // Calls tab: never list canceled jobs (Dispatch tab shows them with a badge)
                          filteredCalls = filteredCalls.filter(
                            (call) => call.status !== 'canceled'
                          );

                          return filteredCalls.length > 0 ? (
                            <>
                              {filteredCalls.slice(0, 4).map((call: Call) => (
                                <CallCard
                                  key={call.id}
                                  call={call}
                                  onStatusChange={handleCallStatusChange}
                                  onCardClick={handleCallClick}
                                />
                              ))}
                              {filteredCalls.length > 4 && (
                                <button
                                  onClick={handleViewAllCalls}
                                  className="w-full mt-3 py-3 px-4 border-2 border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                                >
                                  + View All
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <p>
                                {callsFilterOption === 'Show Completed'
                                  ? 'No calls available'
                                  : callsFilterOption === 'Active Only'
                                    ? 'No active calls at the moment'
                                    : 'No calls found in the last 7 days'}
                              </p>
                              <p className="text-sm mt-1">
                                {callsFilterOption === 'Active Only'
                                  ? 'New calls will appear here'
                                  : 'Try adjusting your filter'}
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Tasks Section: this is the Calls tab, which only someone
                      who only drives has; their own tasks, no one else's */}
                  {(filterOption === 'All' ||
                    filterOption === 'Tasks' ||
                    filterOption === 'Recents') && (
                    <div>
                      <div className="mb-4 flex items-center justify-between">
                        <h2 className="text-2xl font-semibold text-black">
                          Tasks
                        </h2>
                        {filterOption === 'Tasks' && (
                          <div className="relative">
                            <button
                              ref={filterButtonRef}
                              onClick={handleFilterClick}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Filter
                                size={20}
                                className="text-primary"
                                fill="currentColor"
                              />
                            </button>

                            {/* Filter Popup */}
                            {showFilterPopup && (
                              <div
                                data-filter-popup
                                className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[120px]"
                              >
                                {(
                                  ['All', 'Calls', 'Tasks'] as FilterOption[]
                                ).map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => handleFilterSelect(option)}
                                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                      filterOption === option
                                        ? 'bg-primary/10 text-primary font-medium'
                                        : 'text-gray-700'
                                    }`}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        {(() => {
                          // Group tasks by title to handle duplicates
                          const tasksByTitle = new Map<string, Task[]>();
                          tasks.forEach((task) => {
                            const title = task.taskTitle;
                            if (!tasksByTitle.has(title)) {
                              tasksByTitle.set(title, []);
                            }
                            tasksByTitle.get(title)!.push(task);
                          });

                          // For each title group, determine which task to show
                          const tasksToShow: Task[] = [];
                          const today = new Date();
                          today.setHours(0, 0, 0, 0); // Start of today
                          const endOfToday = new Date(today);
                          endOfToday.setHours(23, 59, 59, 999); // End of today

                          tasksByTitle.forEach((titleTasks, title) => {
                            // Find completed task from today
                            const completedToday = titleTasks.find(
                              (task) =>
                                task.status === 'completed' &&
                                task.completedAt &&
                                new Date(task.completedAt) >= today &&
                                new Date(task.completedAt) <= endOfToday
                            );

                            // Find incomplete tasks
                            const incompleteTasks = titleTasks.filter(
                              (task) => task.status !== 'completed'
                            );

                            if (completedToday) {
                              // If there's a completed task from today, show only that one
                              tasksToShow.push(completedToday);
                              console.log(
                                `🔄 [Tasks] Showing completed task for "${title}":`,
                                completedToday.id
                              );
                            } else if (incompleteTasks.length > 0) {
                              // If no completed task from today, show incomplete tasks
                              tasksToShow.push(...incompleteTasks);
                              console.log(
                                `🔄 [Tasks] Showing incomplete tasks for "${title}":`,
                                incompleteTasks.map((t) => t.id)
                              );
                            }
                          });

                          // Sort tasks by due date (soonest first)
                          const sortedTasks = tasksToShow.sort((a, b) => {
                            // Handle tasks without due dates (put them at the end)
                            if (!a.dueDate && !b.dueDate) return 0;
                            if (!a.dueDate) return 1;
                            if (!b.dueDate) return -1;

                            const dateA = new Date(a.dueDate);
                            const dateB = new Date(b.dueDate);
                            return dateA.getTime() - dateB.getTime();
                          });

                          return sortedTasks.length > 0 ? (
                            <>
                              {sortedTasks.slice(0, 6).map((task: Task) => (
                                <TaskCard
                                  key={task.id}
                                  task={task}
                                  onTaskComplete={handleTaskComplete}
                                  onTaskClick={handleTaskClick}
                                />
                              ))}
                              {sortedTasks.length > 6 && (
                                <button
                                  onClick={handleViewAllTasks}
                                  className="w-full mt-3 py-3 px-4 border-2 border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
                                >
                                  + View All
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <p>No active tasks</p>
                              <p className="text-sm mt-1">
                                Tasks will appear here when assigned
                              </p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );

            case 'invoices':
              return (
                <div className="px-4 py-4">
                  {/* Header with Sort */}
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-semibold text-black">
                      Invoices
                    </h2>
                    <div className="relative">
                      <button
                        ref={invoiceSortButtonRef}
                        onClick={handleInvoiceSortClick}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ArrowUpDown size={20} className="text-gray-600" />
                      </button>

                      {/* Sort Popup */}
                      {showInvoiceSortPopup && (
                        <div
                          data-sort-popup
                          className="absolute right-0 top-full mt-1 bg-background border border-gray-200 rounded-xl shadow-xl z-50 min-w-[160px]"
                        >
                          {(['Latest', 'Oldest'] as const).map((option) => (
                            <button
                              key={option}
                              onClick={() => handleInvoiceSortSelect(option)}
                              className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                                invoiceSortOption === option
                                  ? 'bg-primary/10 text-primary font-medium'
                                  : 'text-gray-700'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Invoices List */}
                  <div className="space-y-3">
                    {canCreateInvoice &&
                    invoiceData?.data &&
                    invoiceData.data.length > 0 ? (
                      invoiceData.data.map((invoice) => (
                        <InvoiceCard
                          key={invoice.id}
                          invoice={invoice}
                          onCardClick={handleInvoiceClick}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {canCreateInvoice ? (
                          <>
                            <p>No invoices at the moment</p>
                            <p className="text-sm mt-1">
                              Invoices will appear here
                            </p>
                          </>
                        ) : permissionsState.isError ? (
                          <PermissionsErrorAlert
                            onRetry={permissionsState.refetch}
                          />
                        ) : permissionsState.isLoading ? null : (
                          <>
                            <p>No data available</p>
                            <p className="text-sm mt-1">
                              You don't have permission to view invoices
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );

            case 'scanner':
              return (
                <div className="px-4 py-4">
                  <h2 className="text-2xl font-semibold text-black mb-4">
                    Scanner
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Take photo of impound QR code to view and edit.
                  </p>

                  {!isScanning ? (
                    <div className="space-y-6">
                      {/* Take Photo Button */}
                      <button
                        onClick={handleTakePhoto}
                        className="w-full bg-primary text-primary-foreground py-4 px-6 rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-3"
                      >
                        <Camera size={20} />
                        {isEmbedded ? 'Scan QR Code' : 'Take Photo'}
                      </button>

                      {/* QR Results Display */}
                      {qrResult && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-lg font-semibold text-black mb-2">
                            Scanned QR Code:
                          </h3>
                          <div className="bg-background rounded border p-3">
                            <code className="text-sm text-gray-800 break-all">
                              {qrResult}
                            </code>
                          </div>

                          {/* Check if QR matches an impound using comprehensive search */}
                          {(() => {
                            // Use the same comprehensive search logic as handleQrScan
                            const allImpounds = impoundData?.data
                              ? impoundData.data.map(mapDriverImpoundToImpound)
                              : [];
                            let matchedImpound = null;
                            let searchAttempts = 0;

                            if (allImpounds.length > 0) {
                              // Strategy 1: Exact ID match
                              searchAttempts++;
                              matchedImpound = allImpounds.find(
                                (impound) => impound.id === qrResult
                              );

                              // Strategy 2: Exact impound number match
                              if (!matchedImpound) {
                                searchAttempts++;
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    impound.impoundNumber === qrResult
                                );
                              }

                              // Strategy 3: Case-insensitive ID match
                              if (!matchedImpound) {
                                searchAttempts++;
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    impound.id.toLowerCase() ===
                                    qrResult.toLowerCase()
                                );
                              }

                              // Strategy 4: Case-insensitive impound number match
                              if (!matchedImpound) {
                                searchAttempts++;
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    impound.impoundNumber.toLowerCase() ===
                                    qrResult.toLowerCase()
                                );
                              }

                              // Strategy 5: Partial ID match (contains)
                              if (!matchedImpound) {
                                searchAttempts++;
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    qrResult.includes(impound.id) ||
                                    impound.id.includes(qrResult)
                                );
                              }

                              // Strategy 6: Partial impound number match (contains)
                              if (!matchedImpound) {
                                searchAttempts++;
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    qrResult.includes(impound.impoundNumber) ||
                                    impound.impoundNumber.includes(qrResult)
                                );
                              }

                              // Strategy 7: Trimmed whitespace match
                              if (!matchedImpound) {
                                searchAttempts++;
                                const trimmedQrResult = qrResult.trim();
                                matchedImpound = allImpounds.find(
                                  (impound) =>
                                    impound.id.trim() === trimmedQrResult ||
                                    impound.impoundNumber.trim() ===
                                      trimmedQrResult
                                );
                              }
                            }

                            if (matchedImpound) {
                              return (
                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                  <div className="flex items-center text-green-700">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">
                                      Impound Found!
                                    </span>
                                  </div>
                                  <p className="text-sm text-green-600 mt-1">
                                    Matched: {matchedImpound.impoundNumber}
                                  </p>
                                  <p className="text-xs text-green-500 mt-1">
                                    Automatically switched to impounds tab
                                  </p>
                                </div>
                              );
                            } else {
                              return (
                                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <div className="flex items-center text-yellow-700">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                                    <span className="text-sm font-medium">
                                      No Match Found
                                    </span>
                                  </div>
                                  <p className="text-sm text-yellow-600 mt-1">
                                    Tried {searchAttempts} search strategies but
                                    no impound found
                                  </p>
                                  <div className="mt-2 text-xs text-yellow-600">
                                    <p>
                                      Available impounds: {allImpounds.length}
                                    </p>
                                    <p>
                                      Check console for detailed debugging info
                                    </p>
                                  </div>
                                </div>
                              );
                            }
                          })()}

                          <button
                            onClick={() => setQrResult('')}
                            className="mt-3 text-sm text-primary hover:text-opacity-80 transition-colors"
                          >
                            Clear Result
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* In embedded mode, show loading state instead of web scanner */}
                      {isEmbedded ? (
                        <div className="text-center py-8">
                          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                          <p className="text-gray-600">Opening QR scanner...</p>
                          <p className="text-sm text-gray-500 mt-2">
                            If the scanner doesn't open, check camera
                            permissions
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* QR Scanner - only show in web mode */}
                          <div
                            className="relative bg-black rounded-lg overflow-hidden"
                            style={{ aspectRatio: '1' }}
                          >
                            <Scanner
                              onScan={handleQrScan}
                              onError={handleQrError}
                              paused={false}
                              formats={['qr_code']}
                              styles={{
                                container: { width: '100%', height: '100%' },
                                video: { objectFit: 'cover' },
                              }}
                            />
                          </div>

                          {/* Stop Scanning Button */}
                          <button
                            onClick={stopScanning}
                            className="w-full bg-gray-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                          >
                            Stop Scanning
                          </button>

                          <p className="text-sm text-gray-500 text-center">
                            Position the QR code within the camera view to scan
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );

            case 'drivers':
              // The tab list comes from the user's role permissions, so an admin or
              // dispatcher who was given this tab gets it too, not just the owner,
              // and an owner the tab was turned off for does not.
              if (!hasDriversScreen) {
                return null;
              }
              if (selectedCallId) {
                return (
                  <QueryClientProvider client={queryClient}>
                    <CallScreen
                      callId={selectedCallId}
                      onBack={() => setSelectedCallId(null)}
                      showHeader={false}
                    />
                  </QueryClientProvider>
                );
              }
              if (showAllDispatch) {
                return (
                  <div>
                    <button
                      onClick={() => setShowAllDispatch(false)}
                      className="absolute top-4 left-4 z-50 text-primary hover:text-primary/80"
                    >
                      ← Back
                    </button>
                    <AllDispatchScreen
                      showHeader={false}
                      onCallClick={(callId) => setSelectedCallId(callId)}
                    />
                  </div>
                );
              }
              return (
                <DriversScreen
                  showHeader={false}
                  activeTab={activeTab}
                  onViewAllDispatch={() => setShowAllDispatch(true)}
                />
              );

            case 'analytics':
              if (!hasAnalyticsScreen) {
                return null;
              }
              if (selectedDriver) {
                return (
                  <DriverPerformanceScreen
                    driverId={selectedDriver.id}
                    driverName={selectedDriver.name}
                    onBack={handleBackFromDriverPerformance}
                  />
                );
              }
              return (
                <AnalyticsScreen
                  showHeader={false}
                  onDriverClick={handleDriverClick}
                />
              );

            default:
              return null;
          }
        })()}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col relative overflow-hidden">
      {/* Header */}
      {showHeader && (
        <Header
          driverName={
            headerTitle ||
            getDriverDisplayName(driver, user?.name || user?.email || 'Driver')
          }
          status={driver?.status || 'unknown'}
          onMenuClick={handleMenuClick}
          onStatusClick={handleStatusClick}
          onStatusChange={canEditStatus ? handleStatusChange : undefined}
          isEmbeddedMode={isEmbedded}
          canEditStatus={canEditStatus}
        />
      )}

      {/* Content */}
      <div
        className={`bg-background z-30 flex-1 rounded-t-3xl transition-all duration-300 overflow-hidden ${
          (activeTab === 'home' ||
            activeTab === 'calls' ||
            activeTab === 'drivers') &&
          (hasHomeScreen || hasDriversScreen)
            ? 'pb-[calc(9rem_+_env(safe-area-inset-bottom))]'
            : 'pb-[calc(5rem_+_env(safe-area-inset-bottom))]'
        } ${
          isEmbedded
            ? 'min-h-[600px]' // Fixed height for WebView compatibility
            : 'min-h-[calc(100vh-200px)]' // Original viewport-based height
        }`}
      >
        {renderTabContent()}
      </div>

      {/* Create Call Button - Fixed at bottom (above navigation) - owner catalog on home/calls */}
      {showHeader &&
        (activeTab === 'home' || activeTab === 'calls') &&
        hasHomeScreen && (
          <CreateCallBar onClick={handleShowCreateCall} label="Create Call" />
        )}

      {/* Bottom Navigation - Fixed at bottom */}
      {showHeader && (
        <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 responsive-container bg-background border-t border-gray-200 z-30 pb-[env(safe-area-inset-bottom)]">
          <BottomNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabs}
          />
        </div>
      )}

      {/* New Call Popup - temporarily disabled
      {newCall && driver && (
        <NewCallPopup
          call={newCall}
          driverStatus={driver.status}
          onStart={handleStartCall}
          onDismiss={handleDismissNewCall}
          isStarting={false}
          activeCalls={calls}
        />
      )} */}

      {/* Mobile App Download Prompt */}
      {showMobileAppPrompt && mobileDeviceInfo && (
        <MobileAppDownloadPrompt
          deviceType={mobileDeviceInfo.type}
          onDismiss={handleMobilePromptDismiss}
        />
      )}

      {/* Settings Overlay */}
      {showSettings && (
        <>
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
              showSettingsOverlay
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            }`}
            onClick={handleCloseSettings}
          />

          {/* Settings Panel */}
          <div
            className={`fixed top-0 right-0 h-full w-full responsive-container text-white z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
              showSettingsOverlay ? 'translate-x-0' : 'translate-x-full'
            }`}
            style={{ backgroundColor: '#252525' }}
          >
            {/* Navigation bar header */}
            <div className="flex items-center justify-between px-4 pt-12 pb-3 border-b border-gray-800">
              <div className="w-8" />
              <h2 className="text-[17px] font-semibold text-white">Settings</h2>
              <button
                onClick={handleCloseSettings}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content — scrollable */}
            <div className="flex-1 overflow-y-auto py-6">
              {/* Constant to hide unfinished/disabled features */}
              {(() => {
                const hideUnfinishedSettings = true;

                return (
                  <>
                    {/* ── Profile row (read-only) ── */}
                    <div className="mx-4 mb-6">
                      <div
                        className="rounded-[10px] overflow-hidden px-4 py-3 flex items-center gap-3"
                        style={{ background: '#1C1C1E' }}
                      >
                        {user?.picture ? (
                          <img
                            src={user.picture}
                            alt="Profile"
                            className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: '#45A17D' }}
                          >
                            <User size={24} className="text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-[17px] font-medium truncate">
                            {getDriverDisplayName(
                              driver,
                              user?.name || 'Driver'
                            )}
                          </div>
                          <div className="text-gray-400 text-[13px] truncate">
                            {driver?.email || user?.email || ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Account section (hidden if hideUnfinishedSettings) ── */}
                    {!hideUnfinishedSettings && (
                      <div className="mx-4 mb-6">
                        <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                          Account
                        </p>
                        <div
                          className="rounded-[10px] overflow-hidden divide-y divide-gray-700"
                          style={{ background: '#1C1C1E' }}
                        >
                          {/* Personal Info — unfinished */}
                          <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{ background: '#636366' }}
                            >
                              <User size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              Personal Info
                            </span>
                            <ChevronRight size={16} className="text-gray-500" />
                          </div>

                          {/* Payment — unfinished, shown only for OWNER role */}
                          {isOwner && (
                            <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                              <span
                                className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                                style={{ background: '#FF9500' }}
                              >
                                <CreditCard size={16} className="text-white" />
                              </span>
                              <span className="flex-1 text-white text-[17px]">
                                Payment
                              </span>
                              <ChevronRight
                                size={16}
                                className="text-gray-500"
                              />
                            </div>
                          )}

                          {/* Schedule — unfinished */}
                          <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{ background: '#5856D6' }}
                            >
                              <Calendar size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              Schedule
                            </span>
                            <ChevronRight size={16} className="text-gray-500" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Permissions section ── */}
                    <div className="mx-4 mb-6">
                      <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                        Permissions
                      </p>
                      <div
                        className="rounded-[10px] overflow-hidden divide-y divide-gray-700"
                        style={{ background: '#1C1C1E' }}
                      >
                        {/* Notifications */}
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={perms.handleNotificationRequest}
                          disabled={perms.isRequestingNotifications}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#FF3B30' }}
                          >
                            <Bell size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Notifications
                          </span>
                          <div
                            className="w-10 h-6 rounded-full flex-shrink-0 flex items-center transition-all"
                            style={{
                              background:
                                perms.permissions.notifications.status ===
                                'granted'
                                  ? '#45A17D'
                                  : '#636366',
                              padding: '2px',
                            }}
                          >
                            <div
                              className="w-5 h-5 rounded-full bg-background transition-transform"
                              style={{
                                transform:
                                  perms.permissions.notifications.status ===
                                  'granted'
                                    ? 'translateX(16px)'
                                    : 'translateX(0)',
                              }}
                            />
                          </div>
                        </button>

                        {/* Camera */}
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={perms.handleCameraRequest}
                          disabled={perms.isRequestingCamera}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#34C759' }}
                          >
                            <Camera size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Camera
                          </span>
                          <div
                            className="w-10 h-6 rounded-full flex-shrink-0 flex items-center transition-all"
                            style={{
                              background:
                                perms.permissions.camera.status === 'granted'
                                  ? '#45A17D'
                                  : '#636366',
                              padding: '2px',
                            }}
                          >
                            <div
                              className="w-5 h-5 rounded-full bg-background transition-transform"
                              style={{
                                transform:
                                  perms.permissions.camera.status === 'granted'
                                    ? 'translateX(16px)'
                                    : 'translateX(0)',
                              }}
                            />
                          </div>
                        </button>

                        {/* Location */}
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={() =>
                            perms.handleLocationRequest(actions.requestLocation)
                          }
                          disabled={perms.isRequestingLocation}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#007AFF' }}
                          >
                            <MapPin size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Location
                          </span>
                          <div
                            className="w-10 h-6 rounded-full flex-shrink-0 flex items-center transition-all"
                            style={{
                              background:
                                perms.permissions.location.status === 'granted'
                                  ? '#45A17D'
                                  : '#636366',
                              padding: '2px',
                            }}
                          >
                            <div
                              className="w-5 h-5 rounded-full bg-background transition-transform"
                              style={{
                                transform:
                                  perms.permissions.location.status ===
                                  'granted'
                                    ? 'translateX(16px)'
                                    : 'translateX(0)',
                              }}
                            />
                          </div>
                        </button>

                        {/* Auto-Accept Jobs — unfinished */}
                        {!hideUnfinishedSettings && (
                          <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{ background: '#FF9500' }}
                            >
                              <CheckCircle size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              Auto-Accept Jobs
                            </span>
                            <div
                              className="w-10 h-6 rounded-full flex-shrink-0 flex items-center"
                              style={{
                                background: '#636366',
                                padding: '2px',
                              }}
                            >
                              <div className="w-5 h-5 rounded-full bg-background" />
                            </div>
                          </div>
                        )}

                        {/* Push Notifications */}
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={perms.handlePushNotificationRequest}
                          disabled={perms.isRequestingPush}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#5856D6' }}
                          >
                            <Bell size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Push Notifications
                          </span>
                          <div
                            className="w-10 h-6 rounded-full flex-shrink-0 flex items-center transition-all"
                            style={{
                              background: perms.permissions.pushNotifications
                                .isSubscribed
                                ? '#45A17D'
                                : '#636366',
                              padding: '2px',
                            }}
                          >
                            <div
                              className="w-5 h-5 rounded-full bg-background transition-transform"
                              style={{
                                transform: perms.permissions.pushNotifications
                                  .isSubscribed
                                  ? 'translateX(16px)'
                                  : 'translateX(0)',
                              }}
                            />
                          </div>
                        </button>

                        {/* iOS Installation Required — non-standalone iOS only */}
                        {perms.iosInstall.isIOS &&
                          !perms.iosInstall.isStandalone && (
                            <button
                              className="flex items-center w-full h-11 px-4 gap-3 text-left"
                              onClick={() => {
                                /* handled by global prompt */
                              }}
                            >
                              <span
                                className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                                style={{ background: '#007AFF' }}
                              >
                                <Smartphone size={16} className="text-white" />
                              </span>
                              <span className="flex-1 text-white text-[17px]">
                                iOS Installation Required
                              </span>
                              <ChevronRight
                                size={16}
                                className="text-gray-500"
                              />
                            </button>
                          )}

                        {/* Refresh Permission Status */}
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={perms.checkPermissions}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#636366' }}
                          >
                            <RefreshCw size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Refresh Permission Status
                          </span>
                          <ChevronRight size={16} className="text-gray-500" />
                        </button>
                      </div>
                    </div>

                    {/* ── Display section (hidden if hideUnfinishedSettings) ── */}
                    {!hideUnfinishedSettings && (
                      <div className="mx-4 mb-6">
                        <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                          Display
                        </p>
                        <div
                          className="rounded-[10px] overflow-hidden divide-y divide-gray-700"
                          style={{ background: '#1C1C1E' }}
                        >
                          {/* Dark Mode — unfinished */}
                          <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{
                                background: '#1C1C1E',
                                border: '1px solid #636366',
                              }}
                            >
                              <Moon size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              Dark Mode
                            </span>
                            <ChevronRight size={16} className="text-gray-500" />
                          </div>

                          {/* App Icon — unfinished */}
                          <div className="flex items-center w-full h-11 px-4 gap-3 opacity-40 cursor-not-allowed">
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{ background: '#45A17D' }}
                            >
                              <AppWindow size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              App Icon
                            </span>
                            <ChevronRight size={16} className="text-gray-500" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Company section: anyone in more than one company. It
                        was owners only, which left a driver in two companies
                        stuck in whichever was selected first. ── */}
                    {userCompaniesResult.companies.length > 1 &&
                      (() => {
                        const { companies, selectedCompanyId, selectCompany } =
                          userCompaniesResult;

                        return (
                          <div className="mx-4 mb-6">
                            <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                              Company
                            </p>
                            <CompanySelect
                              companies={companies}
                              value={selectedCompanyId}
                              onChange={selectCompany}
                            />
                          </div>
                        );
                      })()}

                    {/* ── Privacy section ── */}
                    <div className="mx-4 mb-6">
                      <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                        Privacy
                      </p>
                      <div
                        className="rounded-[10px] overflow-hidden divide-y divide-gray-700"
                        style={{ background: '#1C1C1E' }}
                      >
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={() =>
                            window.open(
                              'https://relaytow.com/privacy-policy',
                              '_blank'
                            )
                          }
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#007AFF' }}
                          >
                            <Shield size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Privacy
                          </span>
                          <ChevronRight size={16} className="text-gray-500" />
                        </button>

                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={() => setShowHelpScreen(true)}
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#34C759' }}
                          >
                            <HelpCircle size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Help
                          </span>
                          <ChevronRight size={16} className="text-gray-500" />
                        </button>

                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={() =>
                            window.open(
                              'https://relaytow.com/privacy-policy',
                              '_blank'
                            )
                          }
                        >
                          <span
                            className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                            style={{ background: '#FF9500' }}
                          >
                            <FileText size={16} className="text-white" />
                          </span>
                          <span className="flex-1 text-white text-[17px]">
                            Terms &amp; Conditions
                          </span>
                          <ChevronRight size={16} className="text-gray-500" />
                        </button>
                      </div>
                    </div>

                    {/* ── The dashboard, for anyone with an office role ── */}
                    {hasOfficeRole && (
                      <div className="mx-4 mb-6">
                        <p className="text-xs text-gray-500 uppercase tracking-wide px-4 mb-1">
                          Dispatch dashboard
                        </p>
                        <div
                          className="rounded-[10px] overflow-hidden"
                          style={{ background: '#1C1C1E' }}
                        >
                          <button
                            className="flex items-center w-full h-11 px-4 gap-3 text-left"
                            onClick={() =>
                              window.open(env.dashboardUrl, '_blank')
                            }
                          >
                            <span
                              className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                              style={{ background: '#45A17D' }}
                            >
                              <AppWindow size={16} className="text-white" />
                            </span>
                            <span className="flex-1 text-white text-[17px]">
                              Go to dashboard
                            </span>
                            <ChevronRight size={16} className="text-gray-500" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* ── Untitled section — Sign Out ── */}
                    <div className="mx-4 mb-6">
                      <div
                        className="rounded-[10px] overflow-hidden"
                        style={{ background: '#1C1C1E' }}
                      >
                        <button
                          className="flex items-center w-full h-11 px-4 gap-3 text-left"
                          onClick={handleSignOut}
                        >
                          <span className="flex-1 text-[#FF3B30] text-[17px]">
                            Sign Out
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* iOS Install Prompt (global) */}
          {perms.iosInstall.showPrompt && (
            <IOSInstallPrompt
              onDismiss={perms.iosInstall.dismissPrompt}
              onInstalled={perms.iosInstall.markAsInstalled}
            />
          )}
        </>
      )}

      {/* Help Screen */}
      {showHelpScreen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 opacity-100"
            onClick={() => setShowHelpScreen(false)}
          />

          {/* Help Panel */}
          <div
            className="fixed top-0 right-0 h-full w-full responsive-container text-white z-50 transform transition-transform duration-300 ease-in-out flex flex-col translate-x-0"
            style={{ backgroundColor: '#252525' }}
          >
            {/* Navigation bar header */}
            <div className="flex items-center justify-between px-4 pt-12 pb-3 border-b border-gray-800">
              <div className="w-8" />
              <h2 className="text-[17px] font-semibold text-white">Help</h2>
              <button
                onClick={() => setShowHelpScreen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content — scrollable */}
            <div className="flex-1 overflow-y-auto py-6 px-4">
              {/* Support Bot Card */}
              <div
                className="mb-6 rounded-[10px] p-4"
                style={{ background: '#1C1C1E' }}
              >
                <h3 className="text-white text-[17px] font-semibold mb-1">
                  Questions?
                </h3>
                <p className="text-gray-400 text-[13px] mb-4">
                  Chat with our support bot
                </p>
                <div
                  className="rounded-[10px] overflow-hidden"
                  style={{ background: '#252525' }}
                >
                  <button
                    className="flex items-center w-full h-11 px-4 gap-3 text-left"
                    onClick={() =>
                      window.open('https://support.relaytow.com', '_blank')
                    }
                  >
                    <span
                      className="w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0"
                      style={{ background: '#007AFF' }}
                    >
                      <HelpCircle size={16} className="text-white" />
                    </span>
                    <span className="flex-1 text-white text-[17px]">
                      Visit support page
                    </span>
                    <ChevronRight size={16} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Danger Zone Card */}
              <div
                className="mb-6 rounded-[10px] p-4"
                style={{ background: '#1C1C1E' }}
              >
                <h3 className="text-[#FF3B30] text-[17px] font-semibold mb-3">
                  Danger zone
                </h3>
                <div className="flex justify-end">
                  <button
                    className="bg-[#FF3B30] text-white py-2 px-4 rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => {
                      /* Delete account handler */
                    }}
                    disabled
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HomeScreen;
